import React, { useEffect, useState, useMemo } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  Calendar,
  MessageSquare,
  Settings,
  Search,
  X,
  Church,
  HandCoins,
  FolderKanban,
  ChevronDown,
  ChevronRight,
  Mail,
  FileText,
  History,
  CreditCard,
  BarChart3,
  Cake,
  Receipt,
  CheckCircle2,
  Lock,
  Send,
  HandHeart,
  Coins,
  Activity,
  Music,
  Headphones,
  Video,
  Mic2,
  Share2,
  Link2,
  CalendarDays,
  GitBranch,
  Clock,
  Headset,
  HeartHandshake,
  FolderHeart,
  WalletCards,
  ArrowRightLeft,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useMerchant } from '../context/MerchantContext';
import { checkAllPermissions, checkAnyPermission, canManageResource } from '../utils/permissionUtils';
import ThemeToggle from '../components/ui/ThemeToggle';
import UserMenu from '../components/ui/UserMenu';
import SubscriptionAlert from '../components/ui/SubscriptionAlert';
import ChurchSelector from '../components/selectors/ChurchSelector';
import BranchSelector from '../components/selectors/BranchSelector';
import { useBranch } from '../context/BranchContext';
import NotificationCenter from '../components/ui/NotificationCenter';
import MobileBottomNav from '../components/ui/MobileBottomNav';
import {usePermission} from '../hooks/usePermission';
import { announcementAPI } from '../services/api';
import { Announcement } from '../types/announcement';
import FeatureAnnouncementModal from '../components/modals/FeatureAnnouncementModal';

interface NavigationItem {
  name: string;
  href?: string;
  icon: any;
  requiresFeature: string | null;
  lockedFeature?: string | null; // Feature required to unlock this menu item
  requiredPermissions?: string[]; // Permissions required to access this item (AND logic)
  children?: NavigationItem[];
}

const MerchantLayout = () => {
  const { user, fetchAndUpdateSubscription } = useAuth();
  const navigate = useNavigate();
  const { hasFeature } = useFeatureFlag();
  const location = useLocation();
  const { selectedMerchantId, setSelectedMerchantId, setSelectedMerchantSubdomain } = useMerchant();
  const { selectedBranch, clearBranchContext, isLockedToBranch, switching } = useBranch();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const activityLogPermission = usePermission('settings.viewActivityLogs');
  const settingsPermission = usePermission('settings.viewSettings');

  // Feature announcement state
  const [activeAnnouncement, setActiveAnnouncement] = useState<Announcement | null>(null);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);

  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const response = await announcementAPI.getActive();
        const announcement = response.data.data.announcement;
        if (announcement) {
          setActiveAnnouncement(announcement);
          setShowAnnouncementModal(true);
        }
      } catch (error) {
        // Silently fail — announcement modal is not critical
        console.error('Failed to fetch announcement:', error);
      }
    };

    fetchAnnouncement();
  }, []);

  const handleDismissAnnouncement = async () => {
    if (!activeAnnouncement) return;
    try {
      await announcementAPI.dismiss(activeAnnouncement._id);
    } catch (error) {
      // Still close even if dismiss API fails
      console.error('Failed to dismiss announcement:', error);
    }
    setShowAnnouncementModal(false);
    setActiveAnnouncement(null);
  };

  // Helper to check if user has permission (all must be present)
  const checkPermission = (permissions?: string[]): boolean => {
    if (!permissions || permissions.length === 0) return true;
    return checkAllPermissions(user, permissions);
  };

  // Helper to check if user has any of the permissions
  const hasAnyPermission = (permissions?: string[]): boolean => {
    if (!permissions || permissions.length === 0) return true;
    return checkAnyPermission(user, permissions);
  };

  // Get subscription details from merchant data
  const subscriptionData = user?.merchant?.subscription;
  const expirationStatus = (subscriptionData as any)?.expirationStatus;
  const planName = subscriptionData?.plan || 'starter';
  const expiryDate = (subscriptionData as any)?.expirationDate;

  // Calculate days until expiry
  const getDaysUntilExpiry = (): number => {
    if (!expiryDate) return 0;
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntilExpiry = getDaysUntilExpiry();

  // ✅ Determine alert status based on subscription expiration status
  const getAlertStatus = (): 'expired' | 'cancelled' | 'expiring-soon' | 'grace-period' | 'auto-downgraded' | null => {
    if (expirationStatus === 'grace-period') return 'grace-period';
    if (expirationStatus === 'auto-downgraded') return 'auto-downgraded';
    if (expirationStatus === 'expired') return 'expired';
    if (expirationStatus === 'cancelled') return 'cancelled';
    if (expirationStatus === 'expiring-soon' || expirationStatus === 'expiring_soon') return 'expiring-soon';
    
    // Fallback: Calculate manually if expirationStatus is missing but we have an expiry date
    if (expiryDate && daysUntilExpiry > 0 && daysUntilExpiry <= 7) {
      return 'expiring-soon';
    }
    if (expiryDate && daysUntilExpiry <= 0) {
      // Check if in grace period (3 days after expiration)
      const daysExpired = Math.abs(daysUntilExpiry);
      if (daysExpired <= 3) {
        return 'grace-period';
      }
      return 'expired';
    }
    
    return null;
  };
  
  // Calculate grace days remaining
  const getGraceDaysRemaining = (): number | null => {
    // Only calculate if in grace period
    if (expirationStatus !== 'grace-period') return null;
    if (!expiryDate) return null;
    
    const daysExpired = Math.abs(daysUntilExpiry);
    const GRACE_PERIOD_DAYS = 3;
    if (daysExpired <= GRACE_PERIOD_DAYS) {
      return GRACE_PERIOD_DAYS - daysExpired;
    }
    return 0;
  };

  // ✅ State for alert visibility
  const [showAlert, setShowAlert] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // ✅ CRITICAL: Refetch subscription status on component mount and periodically
  // Only poll when subscription is in a state that might change (expired, grace-period, expiring-soon)
  useEffect(() => {
    const refetchSubscription = async () => {
      await fetchAndUpdateSubscription();
    };

    // Refetch immediately on mount
    refetchSubscription();

    // Only set up polling if subscription is in a changeable state
    const shouldPoll = 
      expirationStatus === 'grace-period' || 
      expirationStatus === 'expired' || 
      expirationStatus === 'expiring-soon' ||
      expirationStatus === 'expiring_soon';

    if (shouldPoll) {
      // Poll every 5 minutes to catch status changes (grace period -> auto-downgraded)
      const interval = setInterval(refetchSubscription, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [expirationStatus, fetchAndUpdateSubscription]);

  const alertStatus = getAlertStatus();

  // Reset alert visibility when status changes
  useEffect(() => {
    setShowAlert(true);
  }, [expirationStatus]);

  // For super admin: if no merchant is selected, redirect to church selector
  useEffect(() => {
    if (user?.role?.slug === 'super_admin' && !selectedMerchantId) {
      navigate('/select-church');
    }
  }, [user?.role?.slug, selectedMerchantId, navigate]);

  // Handle church selection for super admin
  const handleSelectChurch = (church: any) => {
    setSelectedMerchantId(church._id);
    setSelectedMerchantSubdomain(church.subdomain);
    
    // ✅ Refresh subscription data when switching churches
    fetchAndUpdateSubscription();
  };

  // ✅ Helper function to filter navigation based on features AND permissions
  const filterNavigation = (items: NavigationItem[]): NavigationItem[] => {
    return items
      .filter(item => {
        // Check feature requirement
        if (item.requiresFeature && !hasFeature(item.requiresFeature as any)) {
          return false;
        }

        // Check permission requirement (AND logic - all must be present)
        if (!checkPermission(item.requiredPermissions)) {
          return false;
        }

        return true;
      })
      .map(item => {
        if (item.children) {
          return {
            ...item,
            children: filterNavigation(item.children)
          };
        }
        return item;
      });
  };

  // ✅ UNIFIED NAVIGATION - Single config with permission-based access
  const navigation = useMemo(() => {
    const allNavigation: NavigationItem[] = [
      {
        name: 'Dashboard',
        href: '/dashboard',
        icon: LayoutDashboard,
        requiresFeature: null,
        requiredPermissions: ['dashboard.view']
      },
      {
        name: 'Branches',
        href: '/branches',
        icon: Church,
        requiresFeature: 'branchManagement',
        requiredPermissions: ['branches.view']
      },
      {
        name: 'Departments',
        href: '/departments',
        icon: FolderKanban,
        requiresFeature: 'departmentManagement',
        requiredPermissions: ['departments.view']
      },
      {
        name: 'Members',
        icon: Users,
        requiresFeature: 'memberManagement',
        requiredPermissions: ['members.view'],
        children: [
          {
            name: 'All Members',
            href: '/members/all',
            icon: Users,
            requiresFeature: 'memberManagement',
            requiredPermissions: ['members.viewMembers', 'members.view']
          },
          {
            name: 'Birthdays',
            href: '/members/birthdays',
            icon: Cake,
            requiresFeature: 'memberBirthdays',
            requiredPermissions: ['members.viewBirthdays', 'members.view']
          },
          {
            name: 'Partnership',
            href: '/members/partnership',
            icon: HandHeart,
            requiresFeature: 'memberManagement',
            requiredPermissions: ['members.viewPartnership']
          },
        ]
      },
      {
        name: 'Services & Events',
        icon: Calendar,
        requiresFeature: null,
        requiredPermissions: ['events.view'],
        children: [
          {
            name: 'Services',
            href: '/services',
            icon: Clock,
            requiresFeature: 'eventServices',
            requiredPermissions: ['events.viewServices', 'events.view']
          },
          {
            name: 'Events',
            href: '/events',
            icon: Calendar,
            requiresFeature: 'eventManagement',
            requiredPermissions: ['events.viewEvents', 'events.view']
          },
          {
            name: 'Attendance',
            href: '/attendance',
            icon: CheckCircle2,
            requiresFeature: 'eventAttendance',
            requiredPermissions: ['events.viewAttendance', 'events.view'],
          },
          {
            name: 'Calendar',
            href: '/calendar',
            icon: CalendarDays,
            requiresFeature: 'eventCalendar',
            requiredPermissions: ['events.viewCalendar', 'events.view']
          },
        ]
      },
      {
        name: 'Sermons',
        icon: Music,
        requiresFeature: null,
        requiredPermissions: ['sermons.view'],
        children: [
          {
            name: 'Audio',
            href: '/sermons/audio',
            icon: Headphones,
            requiresFeature: 'audioSermons',
            requiredPermissions: ['sermons.viewAudio', 'sermons.view']
          },
          {
            name: 'Video',
            href: '/sermons/video',
            icon: Video,
            requiresFeature: 'videoSermons',
            requiredPermissions: ['sermons.viewVideo', 'sermons.view']
          },
          {
            name: 'Preachers',
            href: '/sermons/preachers',
            icon: Mic2,
            requiresFeature: 'sermonPreachers',
            requiredPermissions: ['sermons.viewPreachers', 'sermons.view']
          },
          {
            name: 'Distribution',
            href: '/sermons/distribution',
            icon: Music,
            requiresFeature: 'sermonDistribution',
            requiredPermissions: ['sermons.viewDistribution', 'sermons.view']
          }
        ]
      },
      {
        name: 'Givings',
        icon: HandCoins,
        requiresFeature: null,
        requiredPermissions: ['giving.view'],
        children: [
          {
            name: 'Offerings',
            href: '/giving/offerings',
            icon: HeartHandshake,
            requiresFeature: 'givingOfferings',
            requiredPermissions: ['giving.viewOfferings','giving.view'],
          },
          {
            name: 'Tithes',
            href: '/giving/tithes',
            icon: Coins,
            requiresFeature: 'givingTithes',
            requiredPermissions: ['giving.viewTithes', 'giving.view'],
          },
          {
            name: 'Projects',
            href: '/giving/projects',
            icon: FolderHeart,
            requiresFeature: 'givingProjects',
            requiredPermissions: ['giving.viewProjects', 'giving.view'],
          }
        ]
      },
      {
        name: 'Finance',
        icon: WalletCards,
        requiresFeature: null,
        requiredPermissions: ['finance.view'],
        children: [
          {
            name: 'Expenses',
            href: '/finance/expenses',
            icon: Receipt,
            requiresFeature: 'financeExpenses',
            requiredPermissions: ['finance.viewExpenses', 'finance.view'],
          },
          {
            name: 'Transactions',
            href: '/finance/transactions',
            icon: ArrowRightLeft,
            requiresFeature: 'financeTransactions',
            requiredPermissions: ['finance.viewTransactions', 'finance.view'],
          },
          {
            name: 'Reports',
            href: '/finance/reports',
            icon: BarChart3,
            requiresFeature: 'financeReports',
            requiredPermissions: ['finance.viewReports', 'finance.view'],
          },
        ]
      },
      {
        name: 'Messaging',
        icon: MessageSquare,
        requiresFeature: 'smsCommunications',
        requiredPermissions: ['communications.view'],
        children: [
          {
            name: 'Analytics',
            href: '/messaging/analytics',
            icon: BarChart3,
            requiresFeature: 'smsAnalytics',
            requiredPermissions: ['communications.analytics', 'communications.view'],
            lockedFeature: 'smsAnalytics'
          },
          {
            name: 'Send SMS',
            href: '/messaging/send',
            icon: Mail,
            requiresFeature: 'smsSend',
            requiredPermissions: ['communications.sendSMS', 'communications.view'],
            lockedFeature: 'smsSend'
          },
          
          {
            name: 'Templates',
            href: '/messaging/templates',
            icon: FileText,
            requiresFeature: 'smsTemplates',
            requiredPermissions: ['communications.templates', 'communications.view'],
            lockedFeature: 'smsTemplates'
          },
          {
            name: 'Credits',
            href: '/messaging/credits',
            icon: CreditCard,
            requiresFeature: 'smsCredits',
            requiredPermissions: ['communications.smsCredits', 'communications.view'],
            lockedFeature: 'smsCredits'
          },
          {
            name: 'Sender ID',
            href: '/messaging/sender-id',
            icon: Send,
            requiresFeature: 'smsSenderId',
            requiredPermissions: ['communications.smsSenderID', 'communications.view'],
            lockedFeature: 'smsSenderId'
          },
          {
            name: 'History',
            href: '/messaging/history',
            icon: History,
            requiresFeature: 'smsHistory',
            requiredPermissions: ['communications.history', 'communications.view'],
            lockedFeature: 'smsHistory'
          }
        ]
      },
      {
        name: 'Social Media',
        icon: Share2,
        requiresFeature: 'socialMedia',
        requiredPermissions: ['social_media.view'],
        children: [
          {
            name: 'Dashboard',
            href: '/social-media/dashboard',
            icon: LayoutDashboard,
            requiresFeature: 'socialMedia',
            requiredPermissions: ['social_media.viewDashboard']
          },
          {
            name: 'Accounts',
            href: '/social-media/accounts',
            icon: Link2,
            requiresFeature: 'socialMedia',
            requiredPermissions: ['social_media.viewAccounts']
          },
          {
            name: 'Planner',
            href: '/social-media/calendar',
            icon: CalendarDays,
            requiresFeature: 'socialMedia',
            requiredPermissions: ['social_media.viewCalendar']
          },
          {
            name: 'All Posts',
            href: '/social-media/posts',
            icon: FileText,
            requiresFeature: 'socialMedia',
            requiredPermissions: ['social_media.viewPosts']
          },
          {
            name: 'Templates',
            href: '/social-media/templates',
            icon: FileText,
            requiresFeature: 'socialMedia',
            requiredPermissions: ['social_media.viewTemplates']
          },
          {
            name: 'Analytics',
            href: '/social-media/analytics',
            icon: BarChart3,
            requiresFeature: 'socialMedia',
            requiredPermissions: ['social_media.viewAnalytics']
          },
        ]
      }
    ];

    // Hide Branches menu when viewing a specific branch
    const filteredNav = allNavigation.filter(item => {
      if (item.name === 'Branches' && (selectedBranch || isLockedToBranch)) return false;
      return true;
    });

    return filterNavigation(filteredNav);
  }, [hasFeature, user?.role?._id, user?.merchant?.id, selectedBranch, isLockedToBranch]);

  //  Initialize expanded menus based on current route
  const getInitialExpandedMenus = (): string[] => {
    const expanded: string[] = [];
    navigation.forEach(item => {
      if (item.children) {
        const hasActiveChild = item.children.some(
          child => child.href && location.pathname.startsWith(child.href)
        );
        if (hasActiveChild) {
          expanded.push(item.name);
        }
      }
    });
    return expanded;
  };

  const [expandedMenus, setExpandedMenus] = useState<string[]>(getInitialExpandedMenus);

  // ✅ FIXED: Auto-expand menu without flickering
  useEffect(() => {
    const menusToExpand = navigation
      .filter(item => item.children && isParentActive(item))
      .map(item => item.name);

    setExpandedMenus(menusToExpand);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  const isParentActive = (item: NavigationItem) => {
    if (item.href && isActive(item.href)) return true;
    if (item.children) {
      return item.children.some(child => child.href && isActive(child.href));
    }
    return false;
  };

  const toggleMenu = (menuName: string) => {
    setExpandedMenus(prev =>
      prev.includes(menuName)
        ? [] // Close the menu if it's already open
        : [menuName] // Close all others and open only this one (accordion behavior)
    );
  };

  const renderNavigationItem = (item: NavigationItem) => {
    const Icon = item.icon;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.includes(item.name);
    const parentActive = isParentActive(item);

    if (hasChildren) {
      return (
        <div key={item.name}>
          {/* Parent Menu Item */}
          <button
            onClick={() => toggleMenu(item.name)}
            className={`
              w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg
              transition-all duration-200
              ${parentActive
                ? 'bg-primary-600/10 text-primary-400'
                : 'text-gray-300 hover:bg-gray-800 hover:text-white'
              }
            `}
          >
            <div className="flex items-center">
              <Icon className="w-5 h-5 mr-3" />
              {item.name}
            </div>
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>

          {/* Submenu Items */}
          {isExpanded && (
            <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-700 pl-4">
              {item.children?.map(child => {
                const ChildIcon = child.icon;
                const childActive = child.href && isActive(child.href);
                const isLocked = child.lockedFeature && !hasFeature(child.lockedFeature as any);

                if (isLocked) {
                  return (
                    <div
                      key={child.name}
                      className="group relative flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg opacity-50 text-gray-500 cursor-not-allowed hover:opacity-60 transition-opacity"
                    >
                      <div className="flex items-center">
                        <ChildIcon className="w-4 h-4 mr-3" />
                        {child.name}
                      </div>
                      <div className="relative flex items-center">
                        <Lock className="w-4 h-4 text-yellow-400 group-hover:scale-125 transition-transform cursor-help" />

                        <div className="absolute right-0 bottom-full mb-3 hidden group-hover:block">
                          {/* Tooltip Container */}
                          <div className="bg-white rounded-lg px-2 py-1 whitespace-nowrap animate-in fade-in zoom-in-95 duration-200">
                            <div className="text-xs bg-white text-black">
                              Upgrade to unlock
                            </div>
                          </div>

                          {/* Arrow Pointer */}
                          <div className="absolute right-3 top-full w-2 h-2 bg-white transform rotate-45 -mt-1 border-r border-b border-gray-200"></div>
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <Link
                    key={child.name}
                    to={child.href || '#'}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      flex items-center px-3 py-2 text-sm font-medium rounded-lg
                      transition-all duration-200
                      ${childActive
                        ? 'bg-primary-600 text-white shadow-primary-600/50'
                        : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                      }
                    `}
                  >
                    <ChildIcon className="w-4 h-4 mr-3" />
                    {child.name}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Single Menu Item (no children)
    return (
      <Link
        key={item.name}
        to={item.href || '#'}
        onClick={() => {
          setSidebarOpen(false);
          setExpandedMenus([]);
        }}
        className={`
          flex items-center px-3 py-2.5 text-sm font-medium rounded-lg
          transition-all duration-200
          ${isActive(item.href || '')
            ? 'bg-primary-600 text-white shadow-primary-600/50'
            : 'text-gray-300 hover:bg-gray-800 hover:text-white'
          }
        `}
      >
        <Icon className="w-5 h-5 mr-3" />
        {item.name}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64
        bg-gray-900 dark:bg-gray-950
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
        flex flex-col
      `}>
        {/* Logo/Church Name - Fixed at top */}
        <div className="h-20 flex items-center px-6 border-b border-gray-800 dark:border-gray-900 flex-shrink-0">
          <div className="flex items-center space-x-3">
            {user?.merchant?.branding?.logo ? (
              <img
                src={user.merchant.branding.logo}
                alt={user.merchant.name}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-bold">
                {user?.merchant?.name?.charAt(0) || 'C'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h2 className="text-white font-semibold text-sm truncate">
                {user?.merchant?.name || 'Church'}
              </h2>
              <p className="text-gray-400 text-xs capitalize">
                {user?.role?.name || 'User'}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation - ✅ With dropdown support */}
        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
          {navigation.map(item => renderNavigationItem(item))}
        </nav>

        {/* Bottom Links - Fixed at bottom */}
        <div className="p-3 border-t border-gray-800 dark:border-gray-900 space-y-1 flex-shrink-0">
          <Link
            to="/support"
            onClick={() => setSidebarOpen(false)}
            className={`
              flex items-center px-3 py-2.5 text-sm font-medium rounded-lg
              transition-all duration-200
              ${isActive('/support')
                ? 'bg-primary-600 text-white'
                : 'text-gray-300 hover:bg-gray-800 hover:text-white'
              }
            `}
          >
            <Headset className="w-5 h-5 mr-3" />
            Support
          </Link>
          {
            (activityLogPermission.hasPermission || activityLogPermission.isSuperAdmin) && (
              <Link
                to="/activity-logs"
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center px-3 py-2.5 text-sm font-medium rounded-lg
                  transition-all duration-200
                  ${isActive('/activity-logs')
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }
                `}
              >
                <Activity className="w-5 h-5 mr-3" />
                Activity Logs
              </Link>
            )
          }
          {
            (settingsPermission.hasPermission || settingsPermission.isSuperAdmin) && (
            <Link
              to="/settings"
              onClick={() => setSidebarOpen(false)}
              className={`
                flex items-center px-3 py-2.5 text-sm font-medium rounded-lg
                transition-all duration-200
                ${isActive('/settings')
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }
              `}
            >
              <Settings className="w-5 h-5 mr-3" />
              Settings
            </Link>
            )
          }
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 lg:ml-64">
        {/* Header - White with search & user */}
        <header className="h-20 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-30 transition-colors">
          <div className="h-full px-4 sm:px-6 lg:px-8 flex items-center justify-between">
            {/* Left: Branch Switcher (mobile) + Search + Church Selector */}
            <div className="flex items-center flex-1 min-w-0 space-x-4">
              {/* Church Selector for Super Admin */}
              {user?.role?.slug === 'super_admin' && (
                <ChurchSelector
                  selectedChurchId={selectedMerchantId || undefined}
                  onSelectChurch={handleSelectChurch}
                  variant="dropdown"
                  className="hidden sm:block"
                />
              )}

              {/* Branch Selector for church_admin and branch_admin */}
              {hasFeature('branchManagement' as any) && user?.role?.slug !== 'super_admin' && (
                <div data-tour="dashboard-branch-filter" className="min-w-0">
                  <BranchSelector className="inline-flex min-w-0" />
                </div>
              )}

              {/* Search Bar */}
              <div className="hidden sm:flex flex-1 max-w-md">
                <div className="relative w-full">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search..."
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-900 border-0 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Right: Theme + Notification + User */}
            <div className="flex items-center space-x-0">
              <ThemeToggle />

              {/* Notification Bell */}
              <NotificationCenter />

              {/* User Menu */}
              <div className="pl-2 border-l border-gray-200 dark:border-gray-700">
                <UserMenu />
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 bg-gray-100 min-h-screen dark:bg-gray-900 overflow-y-auto p-4 pb-24 sm:p-6 sm:pb-24 lg:p-8">
          <div className="max-w-full mx-auto">
            {/* Branch Context Banner */}
            {selectedBranch && (
              <div className="hidden sm:flex mb-4 items-center justify-between px-4 py-2.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-center space-x-2">
                  <GitBranch className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                    Viewing: {selectedBranch.name}
                  </span>
                  <span className="text-xs text-blue-500 dark:text-blue-400">
                    — Data is filtered to this branch
                  </span>
                </div>
                <button
                  onClick={clearBranchContext}
                  className="flex items-center space-x-1 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors"
                >
                  <X className="w-4 h-4" />
                  <span>Clear</span>
                </button>
              </div>
            )}

            {/* ✅ SUBSCRIPTION ALERT - Shows on every page */}
            {alertStatus && showAlert && (
              <div className="mb-6">
                <SubscriptionAlert
                  status={alertStatus}
                  planName={planName}
                  expiryDate={expiryDate}
                  daysUntilRenewal={daysUntilExpiry}
                  graceDaysRemaining={getGraceDaysRemaining() || undefined}
                  onDismiss={() => setShowAlert(false)}
                />
              </div>
            )}
            {/* Loading overlay during branch switch */}
            {switching ? (
              <div className="flex flex-col items-center justify-center py-32">
                <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4" />
                <p className="text-sm text-gray-500 dark:text-gray-400">Switching branch context...</p>
              </div>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={`${location.pathname}-${selectedBranch?._id || 'all'}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                >
                  <Outlet />
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav onOpenMore={() => setSidebarOpen(true)} />

      {/* Feature Announcement Modal */}
      {showAnnouncementModal && activeAnnouncement && (
        <FeatureAnnouncementModal
          announcement={activeAnnouncement}
          onDismiss={handleDismissAnnouncement}
        />
      )}
    </div>
  );
};

export default MerchantLayout;